/**
 * Lightweight aeration plan model used by the live preview on
 * /tools/aeration-calculator. Self-contained — does not call into the
 * full `calculateAerationTiming` engine — so it can re-run on every
 * input change without an engine round-trip.
 *
 * Status (now/soon/optional/wait) factors in three things the legacy
 * engine doesn't take: foot traffic, last-aerated, and current month.
 * The full engine still runs at submit time for the detailed post-submit
 * view.
 */

export type AerationStatus = "now" | "soon" | "optional" | "wait"
export type GrassSeasonGroup = "cool" | "warm"
export type TrafficLevel = "light" | "med" | "heavy"
export type LastAeratedId = "never" | "2y" | "1y" | "6mo"
export type MethodId = "core" | "spike" | "liquid"
export type GrassId =
  | "fescue"
  | "kbg"
  | "rye"
  | "fineFescue"
  | "bermuda"
  | "zoysia"
  | "staug"
  | "centipede"
  | "bahia"
  | "kikuyu"
  | "annualRye"
  | "coolMix"

export interface AerationGrassDef {
  id: GrassId
  name: string
  season: GrassSeasonGroup
  /** 1-indexed months when this grass is in its best aeration window. */
  primary: number[]
  /** 1-indexed months for an acceptable secondary window. */
  secondary: number[]
  optimalSoil: string
  tag: string
}

export const A_GRASSES: Record<GrassId, AerationGrassDef> = {
  fescue: {
    id: "fescue",
    name: "Tall fescue",
    season: "cool",
    primary: [8, 9],
    secondary: [3, 4],
    optimalSoil: "50–65°F",
    tag: "Cool-season",
  },
  kbg: {
    id: "kbg",
    name: "Kentucky bluegrass",
    season: "cool",
    primary: [8, 9],
    secondary: [3, 4],
    optimalSoil: "50–65°F",
    tag: "Cool-season",
  },
  rye: {
    id: "rye",
    name: "Ryegrass",
    season: "cool",
    primary: [8, 9],
    secondary: [3, 4],
    optimalSoil: "50–65°F",
    tag: "Cool-season",
  },
  fineFescue: {
    id: "fineFescue",
    name: "Fine fescue",
    season: "cool",
    primary: [8, 9],
    secondary: [3],
    optimalSoil: "50–60°F",
    tag: "Cool-season",
  },
  bermuda: {
    id: "bermuda",
    name: "Bermudagrass",
    season: "warm",
    primary: [4, 5, 6],
    secondary: [],
    optimalSoil: "70–85°F",
    tag: "Warm-season",
  },
  zoysia: {
    id: "zoysia",
    name: "Zoysia",
    season: "warm",
    primary: [4, 5, 6],
    secondary: [],
    optimalSoil: "70–85°F",
    tag: "Warm-season",
  },
  staug: {
    id: "staug",
    name: "St. Augustine",
    season: "warm",
    primary: [5, 6],
    secondary: [],
    optimalSoil: "75–90°F",
    tag: "Warm-season",
  },
  centipede: {
    id: "centipede",
    name: "Centipede",
    season: "warm",
    primary: [4, 5],
    secondary: [],
    optimalSoil: "70–85°F",
    tag: "Warm-season",
  },
  bahia: {
    id: "bahia",
    name: "Bahiagrass",
    season: "warm",
    primary: [4, 5, 6],
    secondary: [],
    optimalSoil: "70–85°F",
    tag: "Warm-season",
  },
  kikuyu: {
    id: "kikuyu",
    name: "Kikuyugrass",
    season: "warm",
    // Late spring through summer, after full green-up. Pair with dethatching.
    primary: [5, 6, 7],
    secondary: [],
    optimalSoil: "65–75°F",
    tag: "Warm-season",
  },
  annualRye: {
    id: "annualRye",
    name: "Annual ryegrass",
    season: "cool",
    // Aerate the base lawn just before fall overseeding for seed-soil contact.
    primary: [8, 9],
    secondary: [],
    optimalSoil: "55–65°F",
    tag: "Cool-season",
  },
  coolMix: {
    id: "coolMix",
    name: "Cool-season mix",
    season: "cool",
    primary: [8, 9],
    secondary: [3, 4],
    optimalSoil: "55–65°F",
    tag: "Cool-season",
  },
}

// `grassDisplayName` lives in `lib/grass-display.ts` (shared across all tool
// forms). Re-export here for backward-compatibility.
export { grassDisplayName } from "./grass-display"

/**
 * Map our short ids to the canonical grass names used as keys in
 * GRASS_CHARACTERISTICS (and accepted by `calculateAerationTiming`). The
 * `A_GRASSES[id].name` strings are display labels ("Tall fescue", "Zoysia")
 * and don't always match the engine's keys ("Tall Fescue", "Zoysiagrass"),
 * so submitting them straight to the engine throws "Aeration data is not
 * available for this grass type."
 */
export const GRASS_ID_TO_ENGINE_NAME: Record<GrassId, string> = {
  fescue: "Tall Fescue",
  kbg: "Kentucky Bluegrass",
  rye: "Perennial Ryegrass",
  fineFescue: "Fine Fescue",
  bermuda: "Bermudagrass",
  zoysia: "Zoysiagrass",
  staug: "St. Augustinegrass",
  centipede: "Centipede",
  bahia: "Bahiagrass",
  kikuyu: "Kikuyugrass",
  annualRye: "Annual Ryegrass",
  coolMix: "Mixed Cool-Season Blend",
}

/** Match between user-facing GRASS_CHARACTERISTICS names and our short ids. */
export const GRASS_NAME_TO_ID: Record<string, GrassId> = {
  "Tall Fescue": "fescue",
  "Tall fescue": "fescue",
  "Kentucky Bluegrass": "kbg",
  "Kentucky bluegrass": "kbg",
  "Perennial Ryegrass": "rye",
  Ryegrass: "rye",
  "Fine Fescue": "fineFescue",
  "Fine fescue": "fineFescue",
  Bermudagrass: "bermuda",
  Bermuda: "bermuda",
  Zoysiagrass: "zoysia",
  Zoysia: "zoysia",
  "St. Augustinegrass": "staug",
  "St. Augustine": "staug",
  Centipede: "centipede",
  Bahiagrass: "bahia",
  Kikuyugrass: "kikuyu",
  Kikuyu: "kikuyu",
  "Annual Ryegrass": "annualRye",
  "Annual ryegrass": "annualRye",
  "Mixed Cool-Season Blend": "coolMix",
  "Cool-season mix": "coolMix",
}

export interface TrafficLevelDef {
  id: TrafficLevel
  name: string
  blurb: string
  /** Months between aerations at this traffic level. */
  due: number
}

export const A_TRAFFIC: Record<TrafficLevel, TrafficLevelDef> = {
  light: { id: "light", name: "Light", blurb: "Just walked over.", due: 36 },
  med: { id: "med", name: "Medium", blurb: "Kids, pets, mowed weekly.", due: 18 },
  heavy: { id: "heavy", name: "Heavy", blurb: "Sports, parties, frequent traffic.", due: 12 },
}

export interface LastAeratedDef {
  id: LastAeratedId
  label: string
  /** Months since last aeration (large for "never"). */
  months: number
}

export const A_LAST: LastAeratedDef[] = [
  { id: "never", label: "Never", months: 999 },
  { id: "2y", label: "2+ years ago", months: 24 },
  { id: "1y", label: "Last year", months: 12 },
  { id: "6mo", label: "This year", months: 6 },
]

export interface AerationMethodDef {
  id: MethodId
  name: string
  blurb: string
  best?: boolean
}

export const A_METHODS: Record<MethodId, AerationMethodDef> = {
  core: {
    id: "core",
    name: "Core aeration",
    blurb: "Pulls plugs of soil. Most effective.",
    best: true,
  },
  spike: {
    id: "spike",
    name: "Spike aeration",
    blurb: "Pokes holes. Compacts surrounding soil.",
  },
  liquid: {
    id: "liquid",
    name: "Liquid aeration",
    blurb: "Penetrant chemicals. Easier, slower results.",
  },
}

export const MONTH_LABEL = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]
export const MONTH_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

export type MonthKind = "primary" | "secondary" | "off"
export interface MonthCell {
  i: number
  label: string
  kind: MonthKind
}

export interface AerationPlan {
  grass: AerationGrassDef
  months: MonthCell[]
  bestIdx: number
  bestStart: Date
  bestEnd: Date
  status: AerationStatus
  inWindowNow: boolean
  overdue: boolean
  traffic: TrafficLevelDef
  last: LastAeratedDef
}

interface BuildPlanInput {
  grass: GrassId
  traffic: TrafficLevel
  last: LastAeratedId
  /** Optional clock for tests; defaults to `new Date()`. */
  now?: Date
}

export function buildAerationPlan({
  grass,
  traffic,
  last,
  now,
}: BuildPlanInput): AerationPlan {
  const today = now ?? new Date()
  const g = A_GRASSES[grass] ?? A_GRASSES.fescue
  const t = A_TRAFFIC[traffic] ?? A_TRAFFIC.med
  const lastEntry = A_LAST.find(x => x.id === last) ?? A_LAST[0]

  const months: MonthCell[] = MONTH_LABEL.map((label, i) => {
    const m = i + 1
    if (g.primary.includes(m)) return { i, label, kind: "primary" }
    if (g.secondary.includes(m)) return { i, label, kind: "secondary" }
    return { i, label, kind: "off" }
  })

  const thisMonth = today.getMonth() + 1
  let bestIdx = -1
  let daysAway = Infinity

  const considerMonths = (list: number[]) => {
    list.forEach(pm => {
      let diff = pm - thisMonth
      if (diff < 0) diff += 12
      const days = diff * 30
      if (days < daysAway) {
        daysAway = days
        bestIdx = pm - 1
      }
    })
  }
  considerMonths(g.primary)
  if (bestIdx === -1 && g.secondary.length) considerMonths(g.secondary)

  const inWindowNow =
    months[thisMonth - 1].kind === "primary" ||
    months[thisMonth - 1].kind === "secondary"
  const overdue = lastEntry.months >= t.due
  let status: AerationStatus = "wait"
  if (inWindowNow && overdue) status = "now"
  else if (inWindowNow && !overdue) status = "optional"
  else if (overdue) status = "soon"

  // 4-week range mid-month for the best window.
  const yearOffset = bestIdx + 1 < thisMonth ? 1 : 0
  const bestYear = today.getFullYear() + yearOffset
  const bestStart = new Date(bestYear, bestIdx, 10)
  const bestEnd = new Date(bestYear, bestIdx + 1, 0)

  return {
    grass: g,
    months,
    bestIdx,
    bestStart,
    bestEnd,
    status,
    inWindowNow,
    overdue,
    traffic: t,
    last: lastEntry,
  }
}

export function fmtRange(start: Date, end: Date): string {
  return `${MONTH_LABEL[start.getMonth()]} ${start.getDate()} – ${MONTH_LABEL[end.getMonth()]} ${end.getDate()}`
}
